import React, { useRef, useState, useEffect } from 'react';
import { ScrollView, View, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import type { ScrollViewProps, StyleProp, ViewStyle, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useColors } from '../../hooks/useColors';

// Module-level storage for scroll positions (persists across unmounts)
const scrollPositions: Record<string, number> = {};

interface HorizontalScrollViewProps extends Omit<ScrollViewProps, 'horizontal'> {
  // Optional ID to persist scroll position across unmounts
  persistId?: string;
  children: React.ReactNode;
  contentContainerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
}

/**
 * A horizontal ScrollView that works on both mobile and web
 * On web, shows arrow buttons for navigation
 */
export function HorizontalScrollView({ 
  children, 
  contentContainerStyle,
  style,
  persistId,
  ...props 
}: HorizontalScrollViewProps) {
  const scrollRef = useRef<ScrollView>(null);
  const colors = useColors();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  // Initialize currentScrollX from persisted value if available
  const [currentScrollX, setCurrentScrollX] = useState(() => {
    if (persistId && scrollPositions[persistId] !== undefined) {
      return scrollPositions[persistId];
    }
    return 0;
  });
  
  const SCROLL_AMOUNT = 150;
  
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    setCurrentScrollX(contentOffset.x);
    setShowLeftArrow(contentOffset.x > 10);
    setShowRightArrow(contentOffset.x < contentSize.width - layoutMeasurement.width - 10);
    // Persist scroll position for web as well
    if (persistId) {
      scrollPositions[persistId] = contentOffset.x;
    }
  };
  
  const scrollLeft = () => {
    if (scrollRef.current) {
      const newX = Math.max(0, currentScrollX - SCROLL_AMOUNT);
      scrollRef.current.scrollTo({ x: newX, animated: true });
    }
  };
  
  const scrollRight = () => {
    if (scrollRef.current) {
      const newX = currentScrollX + SCROLL_AMOUNT;
      scrollRef.current.scrollTo({ x: newX, animated: true });
    }
  };

  // Restore scroll position on web when content is ready
  useEffect(() => {
    if (Platform.OS === 'web' && persistId && scrollRef.current && contentWidth > 0) {
      const savedPosition = scrollPositions[persistId];
      if (savedPosition && savedPosition > 0) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({ x: savedPosition, animated: false });
        });
      }
    }
  }, [persistId, contentWidth]);

  // On web, add arrow buttons
  if (Platform.OS === 'web') {
    return (
      <View style={[styles.webWrapper, style]}>
        {showLeftArrow && (
          <TouchableOpacity 
            style={[styles.arrowButton, styles.leftArrow, { backgroundColor: colors.background }]}
            onPress={scrollLeft}
            activeOpacity={0.7}
          >
            <ChevronLeft size={20} color={colors.text} />
          </TouchableOpacity>
        )}
        
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          onScroll={handleScroll}
          onContentSizeChange={(w) => setContentWidth(w)}
          onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
          contentContainerStyle={contentContainerStyle}
          style={styles.scrollView}
          {...props}
        >
          {children}
        </ScrollView>
        
        {showRightArrow && contentWidth > containerWidth && (
          <TouchableOpacity 
            style={[styles.arrowButton, styles.rightArrow, { backgroundColor: colors.background }]}
            onPress={scrollRight}
            activeOpacity={0.7}
          >
            <ChevronRight size={20} color={colors.text} />
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // On native, use ScrollView without arrows
  // Get scroll position from module-level storage if persistId is provided
  const savedPosition = persistId ? (scrollPositions[persistId] || 0) : 0;
  
  const handleNativeScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollX = event.nativeEvent.contentOffset.x;
    // Store in module-level storage if persistId is provided
    if (persistId) {
      scrollPositions[persistId] = scrollX;
    }
    props.onScroll?.(event);
  };

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      scrollEventThrottle={16}
      contentContainerStyle={contentContainerStyle}
      style={style}
      onScroll={handleNativeScroll}
      contentOffset={{ x: savedPosition, y: 0 }}
      {...props}
    >
      {children}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  webWrapper: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  arrowButton: {
    position: 'absolute',
    zIndex: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  leftArrow: {
    left: 4,
  },
  rightArrow: {
    right: 4,
  },
});

export default HorizontalScrollView;
